// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { ILogger } from "@lib/logging/ILogger";
import { LogLevel } from "@lib/logging/LogLevel";
import { Assert } from "@lib/utilities/Assert";

type ConsoleLogMethod = ((message: string, ...args: any[]) => void);

const logMethod: ConsoleLogMethod =
  console && typeof console.log === "function"
    ? console.log
    : () => { };

const debugMethod: ConsoleLogMethod =
  console && typeof console.debug === "function"
    ? console.debug
    : logMethod;

const errorMethod: ConsoleLogMethod =
  console && typeof console.error === "function"
    ? console.error
    : logMethod;

const infoMethod: ConsoleLogMethod =
  console && typeof console.info === "function"
    ? console.info
    : logMethod;

const warnMethod: ConsoleLogMethod =
  console && typeof console.warn === "function"
    ? console.warn
    : logMethod;

/**
 * Provides methods for logging to the browsers console.
 * Uses the appropriate console methods for certain log levels if available (console.warn, console.error, ...).
 */
export class ConsoleLogger implements ILogger
{
  enabled = true;
  logLevel: LogLevel;

  constructor(logLevel: LogLevel)
  {
    this.logLevel = logLevel;
  }

  log(logLevel: LogLevel, format: string, args: any[])
  {
    if (!this.enabled || logLevel < this.logLevel)
      return;

    switch (logLevel)
    {
      case LogLevel.Silent:
        return;
      case LogLevel.Error:
        errorMethod(format, ...args);
        return;
      case LogLevel.Warning:
        warnMethod(format, ...args);
        return;
      case LogLevel.Information:
        infoMethod(format, ...args);
        return;
      case LogLevel.Debug:
        debugMethod(format, ...args);
        return;
      case LogLevel.All:
        debugMethod(format, ...args);
        return;
      default:
        Assert.ensureNever(logLevel);
    }
  }
}